import { DateFormatter, DueDateFormatter, Translate } from '@components'

import { InternalAccessor, ItemsPerPage, Table, TableColumn } from '@billy/design-system'
import Cookies from 'js-cookie'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Row } from 'react-table'

import { InvoiceLineDescription } from '@features/invoices/types/invoiceLineDescription'

import { queryClient } from '../../../../../../config/queryClient'
import { CookieKeys } from '../../../../../../enums/cookieKeys'
import { CustomEvent } from '../../../../../../enums/customEvent'
import { EmberRoute } from '../../../../../../enums/emberRoute'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { SortDirection } from '../../../../../../enums/sortDirection'
import { TableIds } from '../../../../../../enums/tableIds'
import { useEmberRouteToUrlGenerator } from '../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useCustomEventListener } from '../../../../../../hooks/useCustomEventListener'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { InvoicesSortProperty } from '../../../../query-api'
import { InvoicesTableAccessor } from '../../enums/invoicesTableAccessor'
import { useInvoices } from '../../hooks/useInvoices'
import { useInvoicesListFilters } from '../../hooks/useInvoicesListFilters'
import { useInvoicesTableData } from '../../hooks/useInvoicesTableData'
import { InvoicesTableRow } from '../../types/invoicesTableRow'
import { EmailStatusIcon } from '../EmailStatusIcon'
import { InvoiceDescription } from '../InvoiceDescription'
import { InvoicesTableHoverActions } from '../InvoicesTableHoverActions'
import { InvoicesTableStaticActions } from '../InvoicesTableStaticActions'
import { InvoiceStatus } from '../InvoiceStatus'

export const InvoicesTable = (): ReactElement => {
  const { invoices, contacts, isLoading, pagination } = useInvoices()
  const tableData = useInvoicesTableData(invoices, contacts)
  const [{ pageSize, sortDirection, sortProperty }, setQueryParams] = useInvoicesListFilters()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const navigate = useNavigate()
  const location = useLocation()

  const columns: TableColumn<InvoicesTableRow>[] = useMemo(
    () => [
      {
        accessor: InvoicesTableAccessor.Status,
        alignment: 'center',
        Cell: ({ value: status }) => <InvoiceStatus status={status} />,
        size: 'xxxxs',
        sortable: false,
      },
      {
        accessor: InvoicesTableAccessor.EmailStatus,
        alignment: 'center',
        Cell: ({ value: sentState }) => <EmailStatusIcon sentState={sentState} />,
        size: 'xxxxs',
        sortable: false,
      },
      {
        accessor: InvoicesTableAccessor.InvoiceNo,
        Cell: ({ value: invoiceNo }) => (invoiceNo ? `#${invoiceNo}` : ''),
        Header: <Translate value="invoices.table.column.invoice_no" />,
        size: 'xs',
        sortable: true,
        truncate: true,
      },
      {
        accessor: InvoicesTableAccessor.OrderNumber,
        Cell: ({ value: orderNumber }) => orderNumber,
        Header: <Translate value="invoices.table.column.order_no" />,
        size: 'xs',
        hidden: true,
        sortable: true,
        truncate: true,
      },
      {
        accessor: InvoicesTableAccessor.Date,
        Cell: ({ value: date }: { value?: string | Date }) => (date ? <DateFormatter value={date} /> : null),
        fixedSize: true,
        Header: <Translate value="invoices.table.column.date" />,
        size: 's',
        sortable: true,
      },
      {
        accessor: InvoicesTableAccessor.DueDate,
        Cell: ({ value: dueDate }: { value?: string | Date }) =>
          dueDate ? <DueDateFormatter value={dueDate} /> : null,
        fixedSize: true,
        Header: <Translate value="invoices.table.column.due_date" />,
        size: 's',
        sortable: true,
        truncate: true,
      },
      {
        // It's well known issue in react-table... Link: https://github.com/TanStack/table/issues/3004
        accessor: (accessors) => accessors[InvoicesTableAccessor.Customer],
        id: InvoicesTableAccessor.Customer,
        Cell: ({ value: customer }) => customer,
        Header: <Translate value="invoices.table.column.customer" />,
        size: 'm',
        sortable: true,
        truncate: true,
      },
      {
        accessor: InvoicesTableAccessor.Description,
        Cell: ({ value: descriptionData }: { value?: InvoiceLineDescription }) =>
          descriptionData ? (
            <InvoiceDescription isCreditNote={descriptionData.isCreditNote} title={descriptionData.value}>
              {descriptionData.value}
            </InvoiceDescription>
          ) : null,
        Header: <Translate value="invoices.table.column.description" />,
        size: 'm',
        sortable: true,
        truncate: true,
      },
      {
        accessor: InvoicesTableAccessor.ExcludedVat,
        alignment: 'right',
        Header: <Translate value="invoices.table.column.excl_vat" />,
        size: 's',
        sortable: true,
      },
      {
        accessor: InvoicesTableAccessor.IncludedVat,
        alignment: 'right',
        Header: <Translate value="invoices.table.column.incl_vat" />,
        hidden: true,
        size: 's',
        sortable: true,
      },
      {
        accessor: InvoicesTableAccessor.Balance,
        alignment: 'right',
        Header: <Translate value="invoices.table.column.balance" />,
        hidden: true,
        size: 's',
        sortable: true,
      },
      {
        accessor: InternalAccessor.HoverActions,
        Header: '',
        Cell: ({ row, data }) => {
          const { invoice } = data[row.id] as InvoicesTableRow

          return <InvoicesTableHoverActions invoice={invoice} />
        },
        sortable: false,
      },
      {
        accessor: InternalAccessor.StaticActions,
        Header: '',
        Cell: ({ row, data }) => {
          const { invoice, isCreditNote, staticActionsItems } = data[row.id] as InvoicesTableRow

          return <InvoicesTableStaticActions items={staticActionsItems} isCreditNote={isCreditNote} invoice={invoice} />
        },
        sortable: false,
      },
    ],
    [],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setQueryParams({ page: 1, pageSize: itemsPerPage })
    },
    [setQueryParams],
  )

  const handleRowClick = useCallback(
    (row: Row<InvoicesTableRow>) => {
      const { id: invoiceId } = row.original
      Cookies.set(CookieKeys.InvoicesListQuery, location.search)

      if (generateUrl) {
        const invoiceUrl = generateUrl(EmberRoute.Invoice, invoiceId)
        navigate(invoiceUrl)
      }
    },
    [generateUrl, navigate, location.search],
  )

  const sortedColumnId = useMemo(() => {
    return getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc)
  }, [sortDirection, sortProperty])

  const handlePageChange = useCallback(
    (page: number) => {
      setQueryParams({ page })
    },
    [setQueryParams],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setQueryParams({
        page: 1,
        sortProperty: columnId as InvoicesSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setQueryParams],
  )

  // Handle ember success state
  const handleInvalidateInvoices = () => {
    queryClient.invalidateQueries([QueryKeys.Invoices, QueryKeys.InvoicesZervant])
  }

  useCustomEventListener(CustomEvent.PaymentSaved, handleInvalidateInvoices)
  useCustomEventListener(CustomEvent.PaymentUpdated, handleInvalidateInvoices)
  useCustomEventListener(CustomEvent.InvoiceUpdated, handleInvalidateInvoices)

  return (
    <Table
      columns={columns}
      currentPage={pagination?.page}
      data={tableData}
      id={TableIds.InvoicesList}
      isLoading={isLoading}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      totalPageItems={pagination?.total}
      withColumnsFiltering
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
